import React from "react";
import { Footer as _Footer } from "antd/es/layout/layout";
import {
  FULL_COMPANY_NAME,
  PERFORMANCE_FEE_DISCLAIMER,
  PRODUCT_TAGLINE,
} from "../helpers/constants";
import { showFooterUi } from "../helpers/general";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { device } from "../lib/breakpoints";
import SolvTaxLogo from "./general/Logo";
import { trackPortalCta } from "../lib/siteActivity";

export default function NavFooter() {
  const location = useLocation();

  const productLinks = [
    { to: "/", label: "Home" },
    { to: "/pricing", label: "Pricing" },
    { to: "/?triggerSignup=true", label: "Request Access", portalCta: true },
    { to: "/help", label: "Help Center" },
  ];

  const companyLinks = [
    { to: "/contact", label: "Contact Us" },
    { to: "/terms-of-use", label: "Terms of Service" },
    { to: "/privacy", label: "Privacy Policy" },
  ];

  return (
    showFooterUi(location) && (
      <Footer>
        <FooterContent>
          <div className="flex flex-col items-center text-center md:items-start md:text-left gap-3">
            <div className="flex items-center justify-center md:justify-start gap-2">
              <SolvTaxLogo />
            </div>
            <p className="text-sm text-slate-400 mt-2 text-center md:text-left max-w-sm">
              {[PRODUCT_TAGLINE, PERFORMANCE_FEE_DISCLAIMER.replace(/\n/g, " ")].join(" ")}
            </p>
          </div>

          <LinkSection>
            <LinkColumn>
              <ColumnTitle>Product</ColumnTitle>
              {productLinks.map((link) => (
                <FooterLink
                  key={link.to}
                  to={link.to}
                  className={location.pathname === link.to ? "active" : ""}
                  onClick={link.portalCta ? () => trackPortalCta("footer") : undefined}
                >
                  {link.label}
                </FooterLink>
              ))}
            </LinkColumn>

            <LinkColumn>
              <ColumnTitle>Company</ColumnTitle>
              {companyLinks.map((link) => (
                <FooterLink
                  key={link.to}
                  to={link.to}
                  className={location.pathname === link.to ? "active" : ""}
                >
                  {link.label}
                </FooterLink>
              ))}
            </LinkColumn>
          </LinkSection>
        </FooterContent>

        <FooterBottom>
          <CopyrightText>
            © {new Date().getFullYear()} {FULL_COMPANY_NAME}. All rights reserved.
          </CopyrightText>
        </FooterBottom>
      </Footer>
    )
  );
}

const Footer = styled(_Footer)`
  padding: 48px 20px 24px;
  background: var(--neutral-50, #f7f9fb);
  border-top: 1px solid var(--neutral-200, #e5e7eb);

  @media (max-width: 767px) {
    padding: 36px 16px 20px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: minmax(240px, 1fr) auto;
    gap: 48px;
    align-items: start;
    justify-content: space-between;
  }
`;

const LinkSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-bottom: 20px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 140px));
    justify-content: center;
  }

  @media (min-width: 768px) {
    grid-template-columns: auto auto;
    column-gap: 120px;
    row-gap: 0;
    justify-content: end;
    margin-bottom: 0;
  }
`;

const LinkColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;

    &:last-child {
      margin-left: 24px;
    }
  }
`;

const ColumnTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: var(--neutral-900, #1f1f1f);
  margin: 0 0 4px 0;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const FooterLink = styled(Link)`
  font-size: 14px;
  color: var(--neutral-600, #4a5568);
  text-decoration: none;
  transition: color 0.2s ease;
  padding: 2px 0;

  &:hover {
    color: var(--primary, #1a4a73);
  }

  &.active {
    color: var(--primary, #1a4a73);
    font-weight: 500;
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 28px;
  margin-top: 8px;
  border-top: 1px solid var(--neutral-200, #e5e7eb);
  text-align: center;
`;

const CopyrightText = styled.p`
  font-size: 12px;
  color: var(--neutral-500, #6b7280);
  margin: 0;
`;
